import { env, API_TIMEOUT_MS } from '@noor/config';

export interface YandexMapKeys {
  /** Yandex Maps v3 JS SDK key. */
  yandex: string;
  /** Yandex Geocoder HTTP API key. */
  geocode: string;
  /** Yandex Suggest API key. */
  suggest: string;
  /** Yandex Places / Organization Search key for ymaps3.search(). */
  search: string;
}

interface MapKeyDto {
  type?: unknown;
  token?: unknown;
}

let keysPromise: Promise<YandexMapKeys> | null = null;
let geocodeKeys: string[] = [];
// Geocoder rejections are scoped to this browser session, not other products.
const blockedGeocodeKeys = new Set<string>();

export function blockYandexGeocodeKey(key: string): void {
  blockedGeocodeKeys.add(key);
}

function fallbackKeys(): YandexMapKeys {
  return {
    // Keep the public client-key fallback usable while the key service is
    // unavailable (for example during local development or a 401 response).
    yandex: env.VITE_YANDEX_MAPS_API_KEY.trim(),
    geocode: env.VITE_YANDEX_GEOCODER_API_KEY,
    suggest: env.VITE_YANDEX_PLACES_API_KEY || env.VITE_YANDEX_GEOCODER_API_KEY,
    search: env.VITE_YANDEX_PLACES_API_KEY || env.VITE_YANDEX_GEOCODER_API_KEY,
  };
}

function normalizeBasicAuth(value: string): string {
  return value.trim().toLowerCase().startsWith('basic ')
    ? value.trim()
    : `Basic ${value.trim()}`;
}

function mapEndpoint(): string {
  const base = env.VITE_CORE_API_URL;
  return `${base.replace(/\/+$/, '')}/api/v1/geocode/map-keys`;
}

/**
 * Backend type names are matched case-insensitively; `geosuggest` is the
 * backend's name for the suggest key. Unknown types are ignored so a new
 * backend row can never clobber a working key.
 */
function normalizeKeyType(type: string): string {
  const normalized = type.trim().toLowerCase();
  if (normalized === 'geosuggest') return 'suggest';
  return normalized;
}

/** Accept a bare row array or the backend's `{data|list|items|keys}` envelope. */
function extractRows(body: unknown): MapKeyDto[] {
  if (Array.isArray(body)) return body as MapKeyDto[];
  if (body !== null && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    for (const key of ['data', 'list', 'items', 'keys']) {
      if (Array.isArray(record[key])) return record[key] as MapKeyDto[];
    }
  }
  return [];
}

function mergeBackendKeys(rows: MapKeyDto[], fallback: YandexMapKeys): YandexMapKeys {
  const next = { ...fallback };
  const activeGeocodeKeys: string[] = [];
  const fromBackend = new Set<string>();
  let hasBackendSearchKey = false;

  for (const row of rows) {
    if (
      !row ||
      typeof row.type !== 'string' ||
      typeof row.token !== 'string' ||
      !row.token.trim()
    ) {
      continue;
    }
    const type = normalizeKeyType(row.type);
    if (type === 'geocode') activeGeocodeKeys.push(row.token.trim());
    if (type === 'javascriptapi') {
      next.yandex = row.token.trim();
      fromBackend.add('yandex');
    } else if (type === 'geocode' || type === 'suggest') {
      next[type] = row.token;
      fromBackend.add(type);
    }
    if (type === 'search' || type === 'places') {
      hasBackendSearchKey = true;
      next.search = row.token;
      fromBackend.add('search');
    }
  }

  if (!hasBackendSearchKey && !env.VITE_YANDEX_PLACES_API_KEY) {
    next.search = next.geocode;
  }

  if (!fromBackend.has('yandex') && env.VITE_YANDEX_MAPS_API_KEY.trim()) {
    // No javascriptapi row from the backend, but a dedicated JS API key is set in the
    // env (VITE_YANDEX_MAPS_API_KEY). It wins over the geocode token below: a
    // Geocoder key is not a JS API key, and api-maps.yandex.ru/v3 rejects it
    // with 403 "Invalid api key" (checked 29.09.2026 on the backend rows).
    next.yandex = env.VITE_YANDEX_MAPS_API_KEY.trim();
    fromBackend.add('yandex');
  }

  if (rows.length > 0 && !fromBackend.has('yandex')) {
    const received = [
      ...new Set(
        rows
          .map((row) => (typeof row.type === 'string' ? row.type : null))
          .filter((type): type is string => type !== null),
      ),
    ];
    console.warn(
      `[map-keys] backend returned key type(s) ${received.join(', ') || 'none'} ` +
        `but none supplies the Maps JS key — the map is disabled.`,
    );
  }

  geocodeKeys = [...new Set(activeGeocodeKeys.reverse())];
  return next;
}

async function fetchBackendKeys(fallback: YandexMapKeys): Promise<YandexMapKeys> {
  const basicAuth = env.VITE_MAP_KEYS_BASIC_AUTH.trim();
  let timeout: ReturnType<typeof setTimeout> | null = null;
  try {
    const controller = new AbortController();
    timeout = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
    const response = await fetch(mapEndpoint(), {
      headers: {
        Accept: 'application/json',
        // Same marker the shared clients attach (this bare fetch bypasses
        // those interceptors) so the backend sees one platform story.
        'X-Platform': 'web',
        ...(basicAuth ? { Authorization: normalizeBasicAuth(basicAuth) } : {}),
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      console.warn(`[map-keys] Backend request failed (${response.status}).`);
      return fallback;
    }

    const body = (await response.json()) as unknown;
    const rows = extractRows(body);
    return rows.length > 0 ? mergeBackendKeys(rows, fallback) : fallback;
  } catch {
    return fallback;
  } finally {
    if (timeout !== null) clearTimeout(timeout);
  }
}

/**
 * Resolve the Maps JS key from the backend's `javascriptapi` row, with a
 * dedicated Maps env key as fallback. Never use a geocoder token for the SDK.
 * Share concurrent requests and cache successful map-key lookups only, so a
 * failed lookup can be retried by the next consumer.
 */
export function getYandexMapKeys(): Promise<YandexMapKeys> {
  keysPromise ??= fetchBackendKeys(fallbackKeys()).then((keys) => {
    if (!keys.yandex) keysPromise = null;
    return keys;
  });
  return keysPromise.then((keys) => ({
    ...keys,
    // Preserve the existing last-row preference, then try earlier active rows.
    // The env fallback applies only when the backend supplied no geocode keys.
    geocode:
      (geocodeKeys.length ? geocodeKeys : [keys.geocode.trim()]).find(
        (key) => key && !blockedGeocodeKeys.has(key),
      ) ?? '',
  }));
}

export function hasYandexMapKeySource(): boolean {
  return Boolean(env.VITE_MAP_KEYS_BASIC_AUTH.trim());
}
